import * as React from 'react';
import { ExpandableGrid } from '@/features/programs/shared/ui/ExpandableGrid';
import CaregiverCard from '@/features/programs/shared/ui/CaregiverCard';
import { HorizontalContainer, VerticalContainer } from '@/components';
import { CaregiverRoleEnum } from '@/shared/enums';
import { FilterButton } from '@/components/buttons/FilterButtons';
import { IDoctorBaseDto } from '@/shared/interfaces';
import { R } from '@/features/programs/resources/ProgramsResources.resx';
import { R as EnumResources } from '@/shared/EnumResources.resx';

const gridClasses =
    'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 justify-center gap-4 transition-[max-height] duration-500 ease-in-out';

type CaregiversGridProps = {
    caregivers: IDoctorBaseDto[];
    backgroundColor?: string;
    showFilters?: boolean;
};

const getButtonText = (role: CaregiverRoleEnum) => {
    switch (role) {
        case CaregiverRoleEnum.Midwife:
            return EnumResources.CaregiverRoleEnum_Midwife_Plural;
        case CaregiverRoleEnum.Dietitian:
            return EnumResources.CaregiverRoleEnum_Dietitian_Plural;
        case CaregiverRoleEnum.Nurse:
            return EnumResources.CaregiverRoleEnum_Nurse_Plural;
        default:
            return '';
    }
};

export const CaregiversGrid: React.FC<CaregiversGridProps> = ({
    caregivers = [],
    backgroundColor,
    showFilters = true,
}) => {
    const roles = [...new Set(caregivers.map((c) => c.CaregiverRole))];
    const [filter, setFilter] = React.useState<CaregiverRoleEnum | null>(null);

    const filteredCaregivers = React.useMemo(() => {
        return caregivers.filter(
            (c) => filter == null || filter === c.CaregiverRole,
        );
    }, [caregivers, filter]);

    return (
        <VerticalContainer spacing="xl">
            {showFilters && (
                <HorizontalContainer spacing="sm" className="flex-wrap">
                    <FilterButton
                        text={R.All}
                        isSelected={filter === null}
                        onClick={() => setFilter(null)}
                        className="cursor-pointer"
                    />

                    {roles.map((role) => {
                        const isSelected = role === filter;
                        return (
                            <FilterButton
                                key={role}
                                text={getButtonText(role)}
                                isSelected={isSelected}
                                onClick={() => setFilter(role)}
                                className="cursor-pointer"
                            />
                        );
                    })}
                </HorizontalContainer>
            )}

            <ExpandableGrid
                items={filteredCaregivers}
                gridClassName={gridClasses}
                backgroundColor={backgroundColor}
            >
                {({ items, isCoveredByOverlay }) =>
                    items.map((caregiver, index) => (
                        <CaregiverCard
                            key={caregiver.UserId}
                            caregiver={caregiver}
                            isCoveredByOverlay={isCoveredByOverlay(index)}
                        />
                    ))
                }
            </ExpandableGrid>
        </VerticalContainer>
    );
};
